import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  CheckedCell,
  Criterion,
  CrudParams,
  DynTableAction,
  DynTableColEditType,
  DynTableColFilterType,
  DynTableColSpan,
  DynTableColType,
  DynTableColumn,
  DynTableConfig,
  DynTableLabels,
  DynTableRowSpan,
  DynTableSearchType,
  JPAPagination
} from './dyn-table.model';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { DomSanitizer } from '@angular/platform-browser';
import { LazyLoadEvent } from 'primeng/api';
import { DatePipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Table } from 'primeng/table';
import { Dialog } from 'primeng/dialog';
import { DynTableService } from './dyn-table.service';
import * as _ from 'lodash-es';

@Component({
  selector: 'dyn-table',
  templateUrl: './dyn-table.component.html',
  styleUrls: ['./dyn-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('rowExpansionTrigger', [
      state(
        'void',
        style({
          transform: 'translateX(-10%)',
          opacity: 0
        })
      ),
      state(
        'active',
        style({
          transform: 'translateX(0)',
          opacity: 1
        })
      ),
      transition('* <=> *', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
    ])
  ]
})
export class DynTableComponent implements OnInit, OnChanges, AfterViewInit {

  currentPagination: Partial<JPAPagination>; // oggetto che tiene traccia della paginazione corrente
  mapCheckboxState: Map<number, any[]>; // mappa che tiene conto delle selezioni con checkbox (x gestire paginazione..)
  checkedCells: CheckedCell[] = []; // lista delle celle checkate
  expandedRows = {};
  formFilter: FormGroup;
  DynTableSearchType = DynTableSearchType;
  DynTableColFilterType = DynTableColFilterType;
  DynTableColEditType = DynTableColEditType;
  DynTableColType = DynTableColType;

  @ViewChild('dynTable') dynTable: Table;
  @ViewChild('dialogCustom') dialogCustom: Dialog;

  @Output() lazyLoadHandler = new EventEmitter(); // evento paginazione
  @Output() notifySelectedListFilter = new EventEmitter<any>(); // emesso quando viene scelto un valore da una select dei filtri
  @Output() routeSelectedValue = new EventEmitter<any>(); // invia oggetto nella forma [value: (valore colonna link), row: (dati completi)]
  @Output() downloadSelectedValue = new EventEmitter<any>();
  @Output() actionButtons = new EventEmitter<any>();
  @Output() callSearchService: EventEmitter<any> = new EventEmitter<any>();
  @Output() resetClicked = new EventEmitter();
  @Output() rowSelected = new EventEmitter<any>();
  @Output() rowUnselected = new EventEmitter<any>();
  @Output() addRowClicked = new EventEmitter<any>();
  @Output() customBtnClicked = new EventEmitter();
  @Output() editableCell = new EventEmitter();
  @Output() iconActionClicked = new EventEmitter<any>();
  @Output() multiselectFilterChange = new EventEmitter<any>();
  @Output() checkedList = new EventEmitter<any>(); // lista dei valori checkati(usato qnd check la singola cella e aggiungo sulla lista checkedCell)
  @Output() rowReorder = new EventEmitter<any>(); // evento usato quando si riordina una riga della tabella
  @Output() focusLosed = new EventEmitter<any>();

  @Input() loading: boolean;
  @Input() config: DynTableConfig; // configurazione della tabella con le varie proprietà
  @Input() columns: Partial<DynTableColumn>[]; // colonne tabella
  @Input() actions: Partial<DynTableAction>[]; // eventuali actions
  @Input() labels: Partial<DynTableLabels>; // mostra le label per ogni colonna
  @Input() colGroup: Partial<DynTableColSpan>[]; // raggruppamento delle colonne della tabella
  @Input() expandedFields: Partial<DynTableColumn>[]; // campi mostrati sul expander
  @Input() rowSpanMetadata: DynTableRowSpan; // info campi rowspan {chiaveCampo: {index => riga da cui parte il match, size => n° righe rowSpan}}

  @Input() data: any[]; // dati e items della tabella
  @Input() totalData: any[]; // aggiunto x totali "globali" --> permette di mantenere la pagination ma far vedere i totali di TUTTE le righe tornate dal WS
  @Input() actionHeader: string; // intestazione della colonna action
  @Input() actionColWidth: number; // larghezza della colonna action(ci possono essere diversi pulsanti)
  @Input() total: number; // il numero totale delle pagine restituite dal servizio
  @Input() filterCollapsed = true; // mostra la sezione dei filtri aperta/chiusa
  @Input() inputPagination: Partial<JPAPagination> = { page: 1, size: 10 }; // paginazione default (10 righe x pagina)
  @Input() showFooter = true; // mostra il footer della tabella

  @Input() stateKey: string; // tiene traccia dello stato della tabella (paginazione corrente, eventuali ordinamenti,..)
  @Input() rowGroup: any; // usato per le somme dei sottogruppi (ad. es i totali per anno)

  @Input() selectedRows?: any[]; // righe selezionate con la multi-select (se config.multiSelectable = true)
  @Input() resetDyntable: boolean; // x poter "resettare" sorting e paginazione della tabella
  @Input() autoLayout = true; // usato per auto-renderizzare la larghezza delle colonne
  @Input() notSameRowChecked: boolean; // flag usato per far si che le celle di tipo "CHECKABLE" non possano essere selezionate su stessa riga
  @Input() addButtonDisabled: boolean; // flag usato per disabilitare il pulsante di add row
  @Input() customButtonDisabled: boolean; // flag usato per disabilitare il pulsante di add row

  constructor(
    public service: DynTableService,
    public sanitizer: DomSanitizer,
    public datepipe: DatePipe,
    public formBuilder: FormBuilder,
    private cdRef: ChangeDetectorRef
  ) {
  }

  ngOnInit() {
    this.createTableForm();
    this.mapCheckboxState = new Map<number, any[]>();
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes.data) {
      // deseleziono in automatico le precedenti selezioni!
      this.data = changes.data.currentValue;
      // this.clearStateSelection(); // svuoto selezioni nello state della tabella
      this.getCheckboxSelectedItems(); // carico selezioni checkbox dalla mappa
      this.expandedRows = {}; // chiudo tutti gli expander
    }
    if (changes.loading) {
      this.loading = changes.loading.currentValue;
    }
    if (changes.inputPagination && changes.inputPagination.currentValue != null) {
      // aggiorno paginazione SOLO SE diversa da null!!
      this.inputPagination = changes.inputPagination.currentValue;
      // IMPORTANTE!!!: fa si che venga aggiornato anche il paginatore...
      if (!changes.inputPagination.isFirstChange()) {
        this.dynTable.first = (this.inputPagination.page - 1) * this.inputPagination.size;
      }
    }
    if (changes.total) {
      this.total = changes.total.currentValue;
    }
    if (changes.config) {
      this.config = changes.config.currentValue;
      // usato per riapplicare i filtri di ricerca dopo il navigation end
      if (this.dynTable) {
        this.formFilterQuery();
      }
    }
    if (changes.labels) {
      this.labels = changes.labels.currentValue;
    }
    if (changes.selectedRows) {
      this.selectedRows = changes.selectedRows.currentValue;
    }
    if (changes.resetDyntable) {
      this.resetDyntable = changes.resetDyntable.currentValue;
      if (!changes.resetDyntable.isFirstChange()) {
        this.tableReset();
      }
    }
    if (changes.addButtonDisabled) {
      this.addButtonDisabled = changes.addButtonDisabled.currentValue;
    }
    if (changes.customButtonDisabled) {
      this.customButtonDisabled = changes.customButtonDisabled.currentValue;
    }
  }

  ngAfterViewInit() {
    // assegno id al wrapper della p-table in modo da poterne cambiare gli stili della scrollbar...
    const tableWrappers = document.getElementsByClassName('ui-table-wrapper');
    if (tableWrappers && tableWrappers.length > 0) {
      // @ts-ignore
      for (const wrapperDiv of tableWrappers) {
        wrapperDiv.id = 'table-wrapper';
      }
    }
  }

  tableReset() {
    this.dynTable.reset();
    if (this.formFilter) {
      this.formFilter.reset();
    }
    this.cdRef.detectChanges();
  }

  /**
   * Evento paginazione (N.B.: lanciato anche al render della tabella se l'array è inizializzato anche vuoto)
   * @param event - contiene dati paginazione / filtri search - sort
   */
  onLazyLoad(event: LazyLoadEvent) {
    const crudParams: CrudParams = this.mapPrimeNgTableParams(Object.assign(event));
    const pagination = this.getPaginationJPA(crudParams);
    this.saveCheckboxSelectedItems(); // PRIMA di cambiare paginazione: salvo eventuali selezioni in pagina con le checkbox
    this.currentPagination = pagination; // poi aggiorno paginazione...

    const search = this.getSearchJPA(crudParams, false);
    const merged = { ...search };
    const filter = {
      filter: merged,
      pagination: pagination
    };
    if (this.data || (!this.data && event.filters && Object.keys(event.filters).length > 0)) {
      this.lazyLoadHandler.emit(filter);
    }
    // controllo se ci sono dei filtri settati. se ci sono li setto sui form corretti(in modo da renderizzarli e visualizzarli correttamente)
    if (Object.keys(event.filters).length > 0) {
      this.filterCollapsed = false; // apro la sezione dei filtri di ricerca(così l'utente può vedere cosa aveva inputato)
      for (const elem of Object.keys(event.filters)) {
        if (event.filters[elem]) {
          // DA CONTROLLARE: se per le select o altri tipi bisogna andare di value[0] o non serve. Altrimenti bisogna
          // fare le varie casistiche if/else
          // se è di tipo DATA o INTERVALLO converto in DATE
          if (event.filters[elem].matchMode.includes('_from') || event.filters[elem].matchMode.includes('_to')) {
            this.formFilter.controls[elem].setValue(new Date(event.filters[elem].value[0]));
          } else {
            this.formFilter.controls[elem].setValue(event.filters[elem].value[0]);
          }

        }
      }
      this.cdRef.detectChanges();
    }
    // fa partire il salvataggio dello state key. di default parte qnd si clicca sulla paginazione. nel nostro caso non parte in automatico
    // qnd si clicca sulla 'ricerca'. per questo bisogna mettere questa forzatura
    if (this.dynTable) {
      this.dynTable.saveState();
    }
  }

  /**
   * Gestione filtri JPA
   *
   */
  public getSearchJPA(crudParams: CrudParams, dontSplitKeys?: boolean) {
    // se ho 1 solo filtro di ricerca impostato entro QUI:
    if (crudParams && crudParams.criterion && crudParams.criterion.name) {
      const filter = {};
      const splitted: string[] = crudParams.criterion.name.split('.');
      if (splitted.length > 1 && !dontSplitKeys) {
        // gestione chiavi "composte" con più punti "."
        this.createJPAFilter(crudParams.criterion, splitted[0] + '.' + splitted[1], filter);
      } else {
        // gestioni chiavi "normali"
        this.createJPAFilter(crudParams.criterion, crudParams.criterion.name, filter);
      }
      return filter;
    }
    // nestedCriterions: se ho più filtri di ricerca impostati entro QUI:
    if (crudParams && crudParams.criterion && crudParams.criterion.nestedCriterions) {
      const filter = {};
      for (const nestedCriterion of crudParams.criterion.nestedCriterions) {
        const splitted: string[] = nestedCriterion.name.split('.');
        if (splitted.length > 1 && !dontSplitKeys) {
          // gestione chiavi "composte" con più punti "."
          this.createJPAFilter(nestedCriterion, splitted[0] + '.' + splitted[1], filter);
        } else {
          // gestioni chiavi "normali"
          this.createJPAFilter(nestedCriterion, nestedCriterion.name, filter);
        }
      }
      return filter;
    }
    return null;
  }

  private createJPAFilter(criterion: Criterion, filterKey: string, filter: any): void {
    if (criterion.type === DynTableColFilterType.DATE_RANGE) {
      // filtro [gte - lte]
      if (criterion.params?.[0] && criterion.params?.[1]) {
        filter[criterion.name + '.greaterOrEqualThan'] = criterion.params?.[0];
        filter[criterion.name + '.lessOrEqualThan'] = criterion.params?.[1];
        return filter;
      }
      if (criterion.params?.[0]) {
        filter[criterion.name + '.greaterOrEqualThan'] = criterion.params?.[0];
        return filter;
      }
    } else if (criterion.type.startsWith(DynTableColFilterType.TIME_RANGE)) {
      if (criterion.type.endsWith('_from')) {
        filter[criterion.name] = criterion.params?.[0];
        return filter;
      }
      if (criterion.type.endsWith('_to')) {
        filter[criterion.name] = criterion.params?.[0];
        return filter;
      }
    } else if (criterion.type.startsWith(DynTableColFilterType.DATE_INTERVAL)) {
      if (criterion.type.endsWith('_from')) {
        filter[criterion.name + '.greaterOrEqualThan'] = this.service.convertDateToMoment(new Date(criterion.params?.[0])).toJSON();
        return filter;
      }
      if (criterion.type.endsWith('_to')) {
        filter[criterion.name + '.lessOrEqualThan'] = this.service.convertDateToMoment(new Date(criterion.params?.[0])).toJSON();
        return filter;
      }
    } else if (criterion.type.startsWith(DynTableColFilterType.TS_INTERVAL)) {
      if (criterion.type.endsWith('_from')) {
        filter[criterion.name + '.greaterOrEqualThan'] = this.service.convertTimestampToMoment(new Date(criterion.params?.[0])).toJSON();
        return filter;
      }
      if (criterion.type.endsWith('_to')) {
        filter[criterion.name + '.lessOrEqualThan'] = this.service.convertTimestampToMoment(new Date(criterion.params?.[0])).toJSON();
        return filter;
      }
    } else {
      // caso "classico" criterion normale: gestisco in base al type
      switch (criterion.type) {
        case DynTableColFilterType.EQUALS:
          filter[filterKey + '.equals'] = criterion.params[0];
          break;
        case DynTableColFilterType.INPUT_CONTAINS:
          filter[filterKey + '.contains'] = criterion.params[0];
          break;
        case DynTableColFilterType.DATE_SEARCH:
          filter[filterKey + '.equals'] = this.service.convertDateToMoment(new Date(criterion.params[0])).toJSON();
          break;
        case DynTableColFilterType.SELECT:
          const keyField: string = this.config.searchFields.find(f => f.name === criterion.name).keyField;
          filter[filterKey + (keyField ? keyField : '') + '.equals'] = criterion.params[0];
          break;
        case DynTableColFilterType.IN_VAL:
          filter[filterKey + '.in'] = criterion.params;
          break;
      }
    }
  }

  /**
   * Compone l'oggetto di paginazione per eseguire chiamate verso i rest
   * @param crudParams - parametri primeNG table
   */
  public getPaginationJPA(crudParams: CrudParams): Partial<JPAPagination> {
    if (crudParams) {
      const targetPage = +crudParams.pagination.startFrom / +crudParams.pagination.size;
      const pagination: JPAPagination = {
        page: targetPage + 1,
        size: +crudParams.pagination.size
      };
      if (crudParams.sorting && crudParams.sorting[0]) {
        const sort = [];
        if (crudParams.sorting[0].substr(0, 1) === '-') {
          sort.push(crudParams.sorting[0].substr(1) + ',desc');
        } else {
          sort.push(crudParams.sorting[0] + ',asc');
        }
        pagination.sort = sort;
      }
      return pagination;
    }
    return null;
  }

  /**
   * Crea il form per i filtri (lato server e lato client) della tabella
   */
  createTableForm() {
    if (this.config && this.config.searchFields && this.config.searchFields.length > 0) {
      const group = {};
      for (const searchField of this.config.searchFields) {
        if (!searchField.range) {
          group[searchField.name] = [null, searchField.validators];
        } else {
          // ad es. filtro date da-a
          group[searchField.nameFrom] = [null, searchField.validators];
          group[searchField.nameTo] = [null, searchField.validators];
        }
      }
      this.formFilter = this.formBuilder.group(group);
      this.formFilter.setValidators(this.atleastOneFieldValidator);
      this.setDefaultOptions();
    } else if (this.config?.clientSideFilters && this.columns) {
      // creo form anche per i filtri client-side così da poterli resettare
      this.formFilter = new FormGroup({});
      this.columns.forEach(col => {
        if (col.searchable) {
          this.formFilter.addControl(col.name, new FormControl());
        }
      });
    }
  }

  /**
   * Applica il validatore che ALMENO UN campo dei filtri deve essere valorizzato x attivare il pulsante "Search"
   */
  atleastOneFieldValidator(formGroup: FormGroup): { [key: string]: any } {
    let isAtLeastOne = false;
    if (formGroup && formGroup.controls) {
      for (const control in formGroup.controls) {
        if (formGroup.controls.hasOwnProperty(control) && formGroup.controls[control].valid && formGroup.controls[control].value) {
          isAtLeastOne = true;
          break;
        }
      }
    }
    return isAtLeastOne ? null : { required: true };
  }

  setDefaultOptions() {
    for (const searchField of this.config.searchFields) {
      if (searchField.defaultOption) {
        this.formFilter.controls[searchField.name].setValue(searchField.defaultOption);
      }
      if (searchField.disabled) {
        this.formFilter.get(searchField.name).disable();
      } else if (searchField.range) {
        if (searchField.defaultValueFrom) {
          this.formFilter.get(searchField.nameFrom).setValue(searchField.defaultValueFrom);
        }
        if (searchField.defaultValueTo) {
          this.formFilter.get(searchField.nameTo).setValue(searchField.defaultValueTo);
        }
        if (!searchField.defaultValueFrom && !searchField.defaultValueTo) {
          // ---> disabilito di default il filtro "nameTo" fino a che non viene selezionato "nameFrom"
          this.formFilter.get(searchField.nameTo).disable();
        }
      }
    }
  }

  actionsPresent(): boolean {
    return (this.actions && this.actions.length > 0);
  }

  // Dimensioni colonna "azioni" --> pixel presi da dyn-table
  calculateActionsWidth(): any {
    const actionExpander: boolean = this.expandedFields && this.expandedFields.length > 0;
    if ((!this.actions || this.actions.length === 0) && actionExpander) {
      // solo expander:
      return { width: '4.5em' };
    } else if (this.actionColWidth) {
      return { width: this.actionColWidth + 'em' };
    } else {
      switch (this.actions.length + (actionExpander ? 1 : 0)) {
        // ci sono anche delle actions aggiuntive:
        case 1:
          return { width: '4.5em' };
        case 2:
          return { width: '7em' };
        case 3:
          return { width: '9em' };
        case 4:
          return { width: '11em' };
        default:
          return { width: '15em' };
      }
    }
  }

  // Dimensione colonna
  getColumnStyle(col): any {
    if (col.style) {
      return col.style;
    }
    return {};
  }

  /**
   * Conferma filtri di ricerca - emetto lazyLoadHandler con valori filtri
   */
  public formFilterQuery() {
    const filters = {};
    const values = this.formFilter ? this.formFilter.value : null;
    if (values) {
      for (const prop in values) {
        if (values.hasOwnProperty(prop)) {
          const matchMode = this.getMatchMode(prop);
          if (values[prop]) {
            filters[prop] = {
              matchMode,
              value: values[prop] instanceof Array ? values[prop] : [values[prop]]
            };
          }
        }
      }
    }
    // controllo i filtri di ricerca. se sono gli stessi dello state key --> mi recupero la paginazione dal localStorage(significa che sto ricaricando 
    // la pagina, probabilmente a fronte di un aggiornamento del record e non voglio perdere la ricarica della pagina con la paginazione corretta),
    // altrimenti resetto e torno alla prima pagina(significa che è stata inserita una nuova ricerca, qndi resetto la paginazione del localStorage)

    // controllo se c'è lo state key salvato sul localStorage
    const objState = JSON.parse(localStorage.getItem(this.stateKey));
    if (filters && Object.keys(filters).length > 0) {
      if (objState && objState.filters) {
        const sameFilters = _.isEqual(filters, objState.filters);
        // rimango sulla paginazione dello statekey, altrimenti resetto e torno alla prima pagina
        if (sameFilters) {
          this.dynTable.first = objState.first;
        } else {
          this.dynTable.first = 0;
        }
      }
    } else {
      if (objState && objState.first) {
        this.dynTable.first = objState.first;
      } else {
        this.dynTable.first = 0;
      }
    }
    this.dynTable.filters = filters;
    const lazyLoadEvent = this.dynTable.createLazyLoadMetadata();
    // setto lo stateKey nel localStorage se presente
    if (filters && Object.keys(filters).length > 0) {
      if (objState) {
        objState.filters = filters;
        localStorage.setItem(this.stateKey, JSON.stringify(objState));
      }
    }
    this.onLazyLoad(lazyLoadEvent);
  }

  getMatchMode(field) {
    for (const searchField of this.config.searchFields) {
      if (searchField.name === field) {
        return searchField.type;
      } else if (searchField.range && searchField.nameFrom === field) {
        return searchField.type + '_from';
      } else if (searchField.range && searchField.nameTo === field) {
        return searchField.type + '_to';
      }
    }
    return DynTableColFilterType.INPUT_CONTAINS; // default
  }

  /**
   * Al reset dei filtri --> invio al chiamante un filtro vuoto così "forzo" il reload di tutti i dati
   */
  resetSearch() {
    if (!this.data) {
      this.data = []; // fa emettere il lazyLoading!
    }
    if (this.config.checkboxes) {
      // rimuovo selezioni con checkbox
      this.clearCheckboxSelections();
    }
    this.resetClicked.emit(); // aggiunto Output x gestione del tasto reset
    this.formFilter.reset();
    // this.setDefaultOptions(); // IMPORTANTE: permette di risettare i valori di default configurati
    this.dynTable.reset();
    localStorage.removeItem(this.stateKey);
    this.resetSelection();
  }

  /**
   * gruppi espandibili
   */
  getExpandedGroups(): any[] {
    const expFields = [];
    for (const entry of this.expandedFields) {
      if (entry.group && !expFields.includes(entry.group)) {
        expFields.push(entry.group);
      }
    }
    return expFields;
  }

  /**
   * campi dei gruppi espandibili
   */
  getExpandedFields(group?: string): any[] {
    const expFields = [];
    for (const entry of this.expandedFields) {
      if (entry.group === group) {
        expFields.push(entry);
      }
    }
    return expFields;
  }

  /**
   * get end emit actions button events
   * @param ev - evento
   * @param type of actions
   * @param rowData data row
   */
  actionHandler(ev, type, rowData) {
    const objEmit = {
      event: ev,
      type,
      rowData
    };
    this.actionButtons.emit(objEmit);
  }

  // metodo usato per selezionare una riga della tabella
  handler(event, row) {
    event.stopPropagation(); // questo fa si che l'evento "click" NON venga propagato anche al click della config (che è nel tag <tr> che contiene le action!!)
    if (this.config.rowClickAction) {
      this.config.rowClickAction(row);
    }
  }

  // metodo usato per abilitare l'action della tabella senza selezionare la riga completa(in modo da evitare la propagazione del evento della riga.
  //  ad.es l'apertura dello storico)
  handlerAction(event, action, row) {
    event.stopPropagation();
    const actionClicked = this.actions.find(el => el.type === action.type);
    actionClicked.onClick(action.type, row);
  }

  // azione usata per aprire solo l'expander senza selezionare tutta la riga della tabella
  actionRowHandler(event, row) {
    event.stopPropagation();
    if (this.actions) {
      this.actions[0].onClick(null, row);
    }
  }

  /**
   * Click sul menu del p-splitbutton
   */
  onMenuClick(event, row) {
    if (row.menuItems) {
      for (const item of row.menuItems) {
        const menuItem = this.actions[0].items.find(k => k.id === item.id);
        menuItem.disabled = item.disabled != null ? item.disabled : false;
        menuItem.visible = item.visible != null ? item.visible : false;
      }
    }
  }

  // mostra il popup in modalità tablet/cell SOLO SE la tabella non è selezionabile (single o multi)
  showCustomDialog(value: any) {
    if (this.dialogCustom && !(this.config.singleSelectable || this.config.multiSelectable)) {
      this.dialogCustom.modal = true;
      this.dialogCustom.dismissableMask = true;
      this.dialogCustom.visible = true;
      this.dialogCustom.responsive = true;
    }
  }

  researchInputValue(value, formControlName) {
    this.callSearchService.emit({ value, name: formControlName });
  }

  /**
   * Selezionato un valore da una select dei filtri
   * @param event - dati dalla select
   * @param id - id della select
   */
  pickedValueFromSelectFilter(event, id) {
    this.notifySelectedListFilter.emit({ value: event?.value, idSelect: id });
  }

  /**
   * Mappatura di tutti i parametri contenuti nell'evento "lazy" di paginazione e/o filtraggio
   */
  public mapPrimeNgTableParams(params: any): CrudParams {
    const page = (params.first + params.rows) / params.rows;
    const criterion = {
      type: null,
      name: null,
      params: null,
      nestedCriterions: null
    };
    if (params.filters) {
      Object.keys(params.filters).forEach(key => {
        if (Object.keys(params.filters).length === 1) {
          // 1 solo filtro impostato
          criterion.type = params.filters[key].matchMode;
          criterion.name = key;
          criterion.params = this.checkParams(params.filters[key].value);
        } else {
          // più filtri impostati --> si mettono in "and"
          if (!criterion.nestedCriterions) {
            criterion.nestedCriterions = [];
          }
          criterion.type = 'and';
          criterion.nestedCriterions.push({
            type: params.filters[key].matchMode,
            name: key,
            params: this.checkParams(params.filters[key].value)
          });
        }
      });
    }
    const pagination = { startFrom: (page - 1) * params.rows, size: params.rows };
    return {
      pagination,
      sorting: params.sortOrder && params.sortField ? [params.sortOrder > 0 ? params.sortField : '-' + params.sortField] : null,
      criterion
    };
  }

  checkParams(oriParam) {
    const outParams = oriParam.slice(0);
    if (outParams.length === 2 && outParams[0] instanceof Date) {
      outParams[0] = this.datepipe.transform(outParams[0], 'yyyy-MM-dd') + ' 00:00:00';
      if (outParams[1] !== null && outParams[1] instanceof Date) {
        outParams[1] = this.datepipe.transform(outParams[1], 'yyyy-MM-dd') + ' 23:59:59';
      } else if (outParams[1] === null) {
        outParams[1] = this.datepipe.transform(outParams[0], 'yyyy-MM-dd') + ' 23:59:59';
      }
    }
    return outParams;
  }

  /**
   * Torna true se abbiamo almeno 1 campo che presenta la prop. "sum"
   */
  hasFooterFields() {
    // se non ci sono dati niente footer
    if (this.data && this.data.length > 0) {
      for (const entry of this.columns) {
        if (entry.sum) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * I campi con sum=true avranno la somma dei valori nel footer (solo numerici)
   * se è presente in input "totalData": la somma verrà fatta su TUTTI i valori
   */
  getFooterFields() {
    const fields = [];
    if (this.expandedFields && this.expandedFields.length > 0) {
      fields.push({ hasFooter: false, style: { width: '65px' } });
    }
    // devo fare le somme per tutte le righe che hanno flag "sum" a true
    for (const entry of this.columns) {
      if (entry.sum) {
        let sum = 0;
        if (this.totalData && this.totalData.length > 0) {
          // se ho "totalData" da input --> faccio la somma globale, altrimenti la faccio x pagina
          for (const row of this.totalData) {
            if (row[entry.name]) {
              sum += Number(row[entry.name]);
            }
          }
        } else {
          for (const row of this.data) {
            if (row[entry.name]) {
              sum += Number(row[entry.name]);
            }
          }
        }
        fields.push({ style: { ...entry.style, ...entry.cellStyle }, hasFooter: true, value: this.service.formatAmount(sum, true) });
      } else {
        fields.push({ style: { ...entry.style, ...entry.cellStyle }, hasFooter: false });
      }
    }
    if (this.actionsPresent()) {
      fields.push({ hasFooter: false, style: { width: '65px' } });
    }
    return fields;
  }

  getSumGrouped(input) {
    const fields = [];
    for (const entry of this.columns) {
      if (entry.grouped) {
        let sum = 0;
        for (const row of this.data) {
          if (input === row.groupBy) {
            sum += Number(row[entry.name]);
          }
        }
        fields.push({ style: { ...entry.style, ...entry.cellStyle }, hasFooter: true, value: this.service.formatAmount(sum, true) });
      } else {
        fields.push({ style: { ...entry.style, ...entry.cellStyle }, hasFooter: false });
      }
    }
    return fields;
  }

  checkColumnType(col) {
    // check x mostrare popup in versione cellulare/tablet
    const dialogTypes = ['custom', 'date', 'datetime', 'euro', 'euroNoDecimal', 'url'];
    return !col.edit && dialogTypes.includes(col.type);
  }

  addRowTable(col) {
    this.addRowClicked.emit(col);
  }

  editedCell(index, col, data) {
    this.editableCell.emit({ index: index, col, data });
  }

  getSelectLabel(editOptions, data) {
    const elem = editOptions ? editOptions.find(op => op.value === data) : null;
    return elem && elem.label ? elem.label : null;
  }

  onBlur(index, col, data) {
    this.focusLosed.emit({ index: index, col, data });
  }

  setFocus(event: Event, cellEditorID: string) {
    if (this.config.editColsFocusDown) {
      const cellEditorHTMLElement: HTMLElement = document.getElementById(cellEditorID);
      if (cellEditorHTMLElement) {
        event.stopPropagation(); // IMPORTANTE! sennò non va con INVIO... (viene riconosciuto come "exit editing" da p-table)
        cellEditorHTMLElement.click();
      }
    }
  }

  getMargin() {
    return this.config && this.config.marginTable ? this.config.marginTable : 'm-0';
  }

  onRowSelect(event) {
    const clickElement: HTMLElement = event.originalEvent.target;
    if (clickElement.classList.contains('link-like') || clickElement.classList.contains('form-control')) {
      // se l'utenza ha cliccato sul link per nuovo tab (identificato dalla classe "link-like")
      // oppure su una cella editabile (classe "form-control") --> non seleziono la riga
      if (this.config.singleSelectable) {
        this.selectedRows = null;
        this.dynTable.selection = this.selectedRows;
      } else if (this.config.multiSelectable) {
        this.selectedRows = this.selectedRows.filter(row => row.index !== event.data.index);
        this.dynTable.selection = this.selectedRows;
      }
    } else {
      // altrimenti seleziono la riga
      this.saveCheckboxSelectedItems();
      this.rowSelected.emit(event.data);
    }
  }

  onRowUnselect(event?) {
    this.saveCheckboxSelectedItems();
    this.rowUnselected.emit(event?.data);
  }

  resetSelection() {
    if (this.config.singleSelectable) {
      this.selectedRows = null;
      this.rowUnselected.emit();
    }
  }

  onRowReorder(event) {
    this.rowReorder.emit(event);
  }

  // caso in cui header checkbox selezionato
  checkboxSelectAll(event) {
    this.saveCheckboxSelectedItems();
    if (event.checked) {
      for (const data of this.data) {
        this.rowSelected.emit(data);
      }
    } else {
      for (const data of this.data) {
        this.rowUnselected.emit(data);
      }
    }
  }

  /**
   * Svuota le selezioni + le selezioni con checkbox
   */
  public clearCheckboxSelections() {
    this.selectedRows = [];
    this.dynTable.selection = []; // IMPORTANTE!!
    this.dynTable.updateSelectionKeys();
    this.mapCheckboxState.clear();
    this.cdRef.detectChanges();
    this.clearStateSelection();
  }

  /**
   * Svuota SOLO le selezioni nello state della tabella
   */
  private clearStateSelection() {
    if (this.stateKey != null) {
      // rimuovo selezione anche dallo state!
      const stateUpdate: any = JSON.parse(localStorage.getItem(this.stateKey));
      if (stateUpdate != null) {
        stateUpdate.selection = [];
        localStorage.setItem(this.stateKey, JSON.stringify(stateUpdate));
      }
    }
  }

  /**
   * Metodo che usa il service x salvare i dati selezionati con eventuali checkbox
   */
  public saveCheckboxSelectedItems() {
    if (this.currentPagination && this.config.checkboxes && this.selectedRows != null) {
      this.mapCheckboxState.set(this.currentPagination.page, this.selectedRows);
    }
  }

  /**
   * Metodo che usa il service x prelevare le righe selezionate con eventuali checkbox per la pagina corrente
   */
  public getCheckboxSelectedItems() {
    if (this.currentPagination && this.config.checkboxes && this.mapCheckboxState) {
      this.selectedRows = this.mapCheckboxState.get(this.currentPagination.page);
    }
  }

  /**
   * Selezione da filtro multiselect
   */
  onMultiselectChange(event, colName: string) {
    this.dynTable.filter(event.value, colName, 'in');
  }

  /**
   * Gestione input dalle maschere filtri "client"
   * @param val - valore dalla maschera
   * @param colName - colonna x cui filtrare la tabella
   * @param searchMethod - metodo di ricerca (contains, equals...)
   */
  searchFromMask(val: string, colName: string, searchMethod: string) {
    const value = val.indexOf('_') !== -1 ? val.substring(0, val.indexOf('_')) : val;
    this.dynTable.filter(value, colName, searchMethod ? searchMethod : 'contains');
  }

  // Gestisce sia caso di campo in tabella di tipo true/false che badge
  searchBoolean(val: boolean, colName: string) {
    const colData: Partial<DynTableColumn> = this.columns.find(col => col.name === colName);
    switch (val) {
      case null:
        this.dynTable.filter(null, colName, 'contains');
        break;
      case true:
        // default badge "SUCCESS"
        this.dynTable.filter(colData.searchTrue ? colData.searchTrue : (colData.type === 'badge' ? 'SUCCESS' : true), colName, 'contains');
        break;
      case false:
        // default badge "DANGER"
        this.dynTable.filter(colData.searchFalse ? colData.searchFalse : (colData.type === 'badge' ? 'DANGER' : false), colName, 'contains');
        break;
    }

  }

  checkValueColumn(event, rowIndex?: any, value?: any, columnName?: string, rowData?: any) {
    if (event.checked) {
      this.checkedCells.push({ index: rowIndex, value: value, columnName: columnName, rowData: rowData });
    } else {
      /* CASI UNCHECK:
      1 -  se unchecko stessa riga, tolgo check colonna diversa
      2-   se unchecko riga diversa e colonna divera, tolgo check della riga e colonna diversa(l'ultima box che unchecko)
      3-   se unchecko riga diversa ma stessa colonna, tolgo la check del'ultima cosa che clicco
      */
      this.checkedCells = this.checkedCells.filter(elem => {
        if (elem.index === rowIndex) {
          return elem.columnName !== columnName;
        } else if (elem.index !== rowIndex && elem.columnName !== columnName) {
          return elem.index !== rowIndex && elem.columnName !== columnName;
        } else if (elem.index !== rowIndex && elem.columnName === columnName) {
          return elem.index !== rowIndex;
        }
      });
    }
    this.checkedList.emit(this.checkedCells);
  }

  checkDisabledCheckedCell(rowIndex: number, colName: string): boolean {
    if (this.notSameRowChecked && this.checkedCells && this.checkedCells.length) {
      return !!this.checkedCells.filter(checked => checked.columnName !== colName).find(checked => checked.index === rowIndex);
    }
    return false;
  }

  /**
   * Fix sort date/timestamp per tabelle gestite lato client (x quelle lato server ci sarà il sort con chiamata REST)
   */
  checkSort(eventSort) {
    if (this.config.clientManagedTable) {
      // trasformo le stringhe in date e sorto tra campi di tipo Date
      const sortedCol: Partial<DynTableColumn> = this.columns.find(col => col.name === eventSort.field);
      if (sortedCol.type === DynTableColType.MOMENT_DATE || sortedCol.type === DynTableColType.MOMENT_TS) {
        this.data = this.data.sort((a, b) => {
          if (eventSort.order === 1) {
            return this.service.convertStringToDate(a[sortedCol.name]).getTime()
              - this.service.convertStringToDate(b[sortedCol.name]).getTime();
          } else {
            return this.service.convertStringToDate(b[sortedCol.name]).getTime()
              - this.service.convertStringToDate(a[sortedCol.name]).getTime();
          }
        });
      }
    }
  }

  // ritorna il n° esatto di colonne contenute nei colGroup (a volte con i rowSpan le colonne sono != dal n° effettivo)
  getColgroupCols(): string[] {
    const colgroupNumber: number = this.colGroup.reduce((a, b) => a + (b.colspan || 0), 0);
    const result = [];
    for (let i = 0; i < colgroupNumber; i++) {
      result.push('');
    }
    return result;
  }

}
